import type { CSSProperties } from 'react'

import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import type { PhaseTone } from '../types'

/**
 * La fase, tal como la escribió el backend.
 *
 * **El texto no se toca.** `stage_label` llega ya traducido
 * (`En revisión con ustedes`, no `Feedback loop`) porque la traducción se hace
 * por categoría del lado del server, donde no depende de cómo renombró el
 * equipo su columna. Acá solo se decide con qué color y qué glifo se dibuja ese
 * texto, y esa decisión vive en un solo lugar: este archivo.
 *
 * **El color sale de `tone`, no del texto.** Antes `STYLE` estaba indexado por
 * el label **en español**, y el label llega en el idioma del contacto: un
 * cliente en `pt` veía `Em desenvolvimento`, ninguna clave matcheaba y el badge
 * caía a gris con un punto genérico. El texto seguía siendo el correcto —así
 * que la pantalla seguía siendo legible— pero el estado pasaba de comunicarse
 * con color + ícono + texto a comunicarse sólo con texto, que es media regla
 * del diseño perdida para dos de los tres idiomas.
 *
 * `stage_tone` es la categoría **de cliente**: no depende del idioma y ya trae
 * `blocked` y `paused` colapsados en uno, porque al cliente no se le cuenta que
 * su proyecto está trabado. Ya viajaba para el proyecto; se agregó para la
 * tarea y para la columna del tablero, que eran los dos que faltaban.
 *
 * Un tono desconocido sigue sin romper nada: sale en gris con un punto. Mejor
 * una fase sin color que una fase mal pintada.
 */

interface PhaseStyle {
  token: string
  glyph: string
}

const STYLE: Record<PhaseTone, PhaseStyle> = {
  not_started: { token: '--phase-not-started', glyph: '○' },
  in_progress: { token: '--phase-in-development', glyph: '▸' },
  review: { token: '--phase-feedback', glyph: '↺' },
  done: { token: '--phase-done', glyph: '✓' },
  paused: { token: '--phase-paused', glyph: '‖' },
}

const ONGOING: PhaseStyle = { token: '--phase-ongoing', glyph: '∞' }
const UNKNOWN: PhaseStyle = { token: '--text-3', glyph: '·' }

export interface PhaseBadgeProps {
  /** El texto, ya traducido por el server. Se muestra tal cual. */
  label: string
  /** La fase como dato. Es lo que decide el color y el glifo. */
  tone?: PhaseTone
  /** Los permanentes no avanzan por fases: son un servicio continuo. */
  isOngoing?: boolean
  size?: 'md' | 'lg'
  className?: string
}

export function PhaseBadge({
  label,
  tone,
  isOngoing = false,
  size = 'md',
  className,
}: PhaseBadgeProps) {
  const t = useT()
  const style = isOngoing ? ONGOING : ((tone && STYLE[tone]) ?? UNKNOWN)

  const colors: CSSProperties = {
    color: `hsl(var(${style.token}))`,
    backgroundColor: `hsl(var(${style.token}) / .12)`,
    borderColor: `hsl(var(${style.token}) / .3)`,
  }

  return (
    <span
      style={colors}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border font-medium',
        size === 'lg' ? 'h-7 px-2.5 text-sm' : 'h-6 px-2 text-xs',
        className,
      )}
    >
      <span aria-hidden="true" className="num leading-none">
        {style.glyph}
      </span>
      {/* `label` sale TAL CUAL: ya viene traducido del server. Lo único nuestro
          es el cartel de los permanentes, que no es una fase. */}
      <span className="truncate">{isOngoing ? t('portal.phase.ongoing') : label}</span>
    </span>
  )
}
